import {animate, query, style, transition, trigger,} from '@angular/animations';
import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  OnInit,
  ViewChild,
} from '@angular/core';
import {ActivatedRoute, NavigationEnd, NavigationStart, Router, RouterOutlet,} from '@angular/router';
import {NgbConfig, NgbModalConfig} from '@ng-bootstrap/ng-bootstrap';
import {select, Store,} from '@ngrx/store';
import {BrowserTabTitleService} from 'core/services/browser-tab-title.service';

import {CookieNotificationService} from 'core/services/cookie.service';
import {Subscription} from 'rxjs';
import {filter} from 'rxjs/operators';
import {ResetResourceFacetsAction} from 'search/actions/facet.actions';
import {setFirstNavigationId} from 'search/actions/history.actions';
import {getFirstPageId} from 'search/reducers/history.reducer';
import {State} from 'search/reducers/root.reducer';
import {ConfigurationLoader} from 'shared/configuration-loader';
import {toggleOpenedLibraryInfoWidget} from 'user/actions/user-profile.actions';
import {getLibraryInfoWidgetState, getUserPanelState,} from 'user/reducers/user.reducer';
import {getGlobalSidebarVisibility} from 'core/reducers/global-sidebar.reducer';
import {environment} from '../../../../environments/environment';
import {FeatureToggleService} from 'app/services/feature-toggle.service';

@Component({
  styleUrls: ['./root-page.component.scss'],
  selector: 'app-root',
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './root-page.component.html',
  animations: [
    trigger('animatePath', [
      transition('home => search', [
        query(':enter', [
          style({opacity: 0, position: 'absolute'}),
        ]),
        query(':leave', [
          animate('300ms ease-out', style({marginLeft: '288px'})),
        ]),
      ]),
      transition('search => home', [
        query(':enter', [
          style({marginLeft: '288px'}),
          animate('300ms ease-out', style({marginLeft: '0px'})),
        ]),
      ]),
    ]),
  ],
})
export class RootPageComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('root', {static: true}) public root: ElementRef;
  @ViewChild('outlet', {static: true}) public outlet: RouterOutlet;
  @ViewChild('main', {static: true}) public main: ElementRef;
  @ViewChild('header') header!: ElementRef;
  public url: string;
  public seen: boolean;
  public isPortraitNotificationDisabled = false;
  public firstPageId: number;
  public animationState: string;
  public isPanelShown: boolean;
  public isLibraryInfoShown: boolean;
  public isBookshelfDisplayed = false;
  public isGlobalSidebarVisible: boolean;
  public fixedHeader: boolean;
  public pageName: string = 'home-page';
  private subscriptions: Subscription = new Subscription();
  public featureOneTrustBanner: boolean = false;
  public accountPortal: boolean = false;
  public isAccountPortalBookmark: boolean = false;
  public apHeaderHeight: number = 0;

  constructor(
    private router: Router,
    private cookieService: CookieNotificationService,
    private store: Store<State>,
    private titleService: BrowserTabTitleService,
    private route: ActivatedRoute,
    private chRef: ChangeDetectorRef,
    private ngbModalConfig: NgbModalConfig,
    private readonly ngbConfig: NgbConfig,
    private readonly configLoader: ConfigurationLoader,
    private readonly featureToggleService: FeatureToggleService,
  ) {
  }

  public ngOnInit() {
    this.url = this.router.url;
    this.titleService.setTabTitle();
    const screenHeight = window.innerHeight;

    this.fixedHeader = screenHeight >= 400
      ? this.configLoader.headerConfig?.fixedHeader
      : false;

    // Add resize listener to update fixed header state
    window.addEventListener('resize', () => {
      const newScreenHeight = window.innerHeight;
      this.fixedHeader = newScreenHeight >= 400
        ? this.configLoader.headerConfig?.fixedHeader
        : false;
      this.chRef.detectChanges();
    });

    if (environment.disableAnimation) {
      this.ngbConfig.animation = false;
    }

    this.ngbModalConfig.centered = true;
    this.ngbModalConfig.windowClass = 'inspire-custom-modal';
    this.addStoreSubscriptions();
    this.addRouterEventsSubscriptions();

    // Theme modification
    this.pageName = this.getPageClass(this.url);
    // END: Theme modification
    this.accountPortal = this.featureToggleService.getToggles()['VE-5143_2025-12-31_Account-Portal'];
    this.isAccountPortalBookmark = this.featureToggleService.getToggles()['VE-5143_2025-12-31_portal-bookmarks'];

    this.featureOneTrustBanner = this.featureToggleService.getToggles()['VE-3951_2024-04-30_discover-one-trust-banner'];
    if (!this.featureOneTrustBanner) {
      this.subscriptions.add(
        this.cookieService.seen$.subscribe((data) => {
            this.seen = data;
          }
        ));
    }

    window.addEventListener('scroll', this.onScroll, true);

    this.setApAsideHeaderHeight();
  }

  ngAfterViewInit(): void {
    this.apHeaderHeight = this.header.nativeElement.offsetHeight;
  }

  public setApAsideHeaderHeight() {
    if (!this.fixedHeader && this?.header?.nativeElement) {
      if (window.scrollY > 0) {
        if (this.header.nativeElement.offsetHeight - window.scrollY < 0) {
          this.apHeaderHeight = 0;
        } else {
          this.apHeaderHeight = this.header.nativeElement.offsetHeight - window.scrollY;
        }
      } else {
        this.apHeaderHeight = this.header.nativeElement.offsetHeight;
      }
      this.chRef.detectChanges();
    }
  }

  onScroll = () => {
    // Handle scroll event
    this.setApAsideHeaderHeight();
  };

  public addRouterEventsSubscriptions() {
    this.subscriptions.add(this.router.events.pipe(filter((event) => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        this.pageName = this.getPageClass(event.urlAfterRedirects);
        this.accountPortal = this.featureToggleService.getToggles()['VE-5143_2025-12-31_Account-Portal'];

        this.setDefaultTabTitle(event.url);

        this.url = event.url;

        if (!/^\/search\/card(\W|)/.test(event.url)) {
          this.animationState = this.outlet.activatedRouteData.page;
        }
      }));

    this.subscriptions.add(this.router.events.pipe(filter((event) => event instanceof NavigationStart))
      .subscribe((event: NavigationStart) => {
        if (event.navigationTrigger === 'popstate') {
          if (this.firstPageId === event.restoredState?.navigationId) {
            this.store.dispatch(setFirstNavigationId({navId: event.id}));
          }
        }
        if ((event.url === this.url) && (event.id - this.firstPageId === 1)) {
          this.store.dispatch(setFirstNavigationId({navId: event.id}));
        }
      }));

    this.subscriptions.add(this.route.queryParams.subscribe((params: any) => {
      if (params.libraryInfoWidget === 'opened') {
        this.store.dispatch(toggleOpenedLibraryInfoWidget({isOpened: true}));
      }
    }));
  }

  public addStoreSubscriptions() {
    this.subscriptions.add(this.store.pipe(select(getFirstPageId)).subscribe((id) => {
      this.firstPageId = id;
    }));
    this.subscriptions.add(this.store.pipe(select(getUserPanelState)).subscribe((isOpened) => {
      this.isPanelShown = isOpened;
      this.chRef.markForCheck();
    }));
    this.subscriptions.add(this.store.pipe(select(getLibraryInfoWidgetState)).subscribe((isOpened) => {
      this.isLibraryInfoShown = isOpened;
    }));
    this.subscriptions.add(this.store.pipe(select(getGlobalSidebarVisibility)).subscribe((isVisible) => {
      this.isGlobalSidebarVisible = isVisible;
    }));
  }

  public getPageClass(url: string) {
    return url.includes('card') ? 'details' :
      url.includes('search') ? 'search' :
        url.includes('portal') ? 'portal' : 'home-page';
  }

  public setDefaultTabTitle(url: string) {
    if (['/', '/search'].includes(url)) {
      this.titleService.setTabTitle();
    }
  }

  public resetResourceFacets() {
    this.store.dispatch(new ResetResourceFacetsAction());
  }

  public skipToElement(element: string) {
    (this as any)[element].nativeElement.focus();
  }

  public onPortraitNotificationClick() {
    this.isPortraitNotificationDisabled = true;
  }

  public ngOnDestroy() {

    window.removeEventListener('scroll', this.onScroll, true);

    this.subscriptions.unsubscribe();
    window.removeEventListener('resize', () => {
      const newScreenHeight = window.innerHeight;
      this.fixedHeader = newScreenHeight >= 400
        ? this.configLoader.headerConfig?.fixedHeader
        : false;
    });
  }
}
